import React, { useState } from 'react';
import { ArrowLeft, Calendar, Users, Code, CheckCircle, Database, Shield, MessageCircle, Building2, Target, Award, TrendingUp, ExternalLink, Zap, Box, Layers, Lock, Bell, BarChart3, FileText, Settings } from 'lucide-react';

export default function SupemirProject({ darkMode, language, onBack }) {
  const [activeTab, setActiveTab] = useState('presentation');
  const isRTL = language === 'ar';

  const content = {
    fr: {
      title: 'Plateforme de Gestion Scolaire SUPEMIR',
      subtitle: 'Solution ERP Éducative Professionnelle',
      backButton: 'Retour aux projets',
      
      tabPresentation: 'Présentation',
      tabAboutSchool: 'SUPEMIR',
      tabDevelopment: 'Développement',
      tabArchitecture: 'Architecture',
      tabFeatures: 'Fonctionnalités',
      tabGallery: 'Galerie',
      tabResults: 'Résultats',
      
      // Présentation
      presentationTitle: 'Contexte & Justification du Projet',
      presentationIntro: 'SUPEMIR, établissement d\'enseignement supérieur reconnu depuis 2004, gérait ses opérations avec des processus manuels et des outils fragmentés. Face à une croissance continue (500+ étudiants, 25+ professeurs) et à la complexité croissante des tâches administratives, l\'école a identifié un besoin urgent de digitalisation complète de ses systèmes.',
      
      trustSection: 'Un Projet Basé sur la Confiance',
      trustText: 'SUPEMIR m\'a confié ce projet stratégique majeur, démontrant une confiance totale en mes compétences. Cette école qui ne produit que l\'excellence a choisi de faire appel à l\'un de ses propres talents pour développer cette solution critique. Ce choix reflète la philosophie de SUPEMIR : croire en ses étudiants et leur donner l\'opportunité de prouver leur valeur sur des projets d\'envergure réelle.',
      
      proudMoment: 'Fierté & Reconnaissance',
      proudText: 'C\'est un immense honneur d\'avoir été sélectionné pour développer une plateforme aussi stratégique pour mon école. SUPEMIR a fait le choix audacieux de confier l\'intégralité de ce projet à un développeur issu de ses propres rangs - un produit 100% SUPEMIR développé par un étudiant SUPEMIR. Cette confiance témoigne de la qualité de la formation dispensée et de la capacité de l\'école à produire des talents opérationnels capables de gérer des projets professionnels complexes.',
      
      challengesTitle: 'Problématiques Identifiées',
      challenges: [
        'Gestion manuelle des inscriptions entraînant des erreurs et pertes de temps',
        'Absence de système centralisé pour le suivi académique et financier',
        'Communication inefficace entre administration, professeurs et étudiants',
        'Difficultés de génération de rapports et statistiques en temps réel',
        'Processus de paiement manuel source d\'erreurs comptables',
        'Manque de traçabilité et d\'historique des opérations'
      ],
      
      solutionTitle: 'La Solution Développée',
      solutionText: 'J\'ai conçu et développé une plateforme ERP éducative complète, intégrée et sur-mesure qui répond précisément aux besoins de SUPEMIR. Cette solution digitale centralise l\'ensemble des processus de l\'école dans un système unique, sécurisé et accessible 24/7.',
      
      projectInfoTitle: 'Informations Projet',
      projectCommercial: 'Projet Commercial - Solution Vendue à SUPEMIR',
      projectDuration: 'Durée: 4 mois de développement intensif',
      projectTeam: 'Développement: Solo avec support équipe IT SUPEMIR',
      projectStatus: 'Statut: Terminé et déployé en production',
      
      // SUPEMIR
      aboutSupemir: 'À Propos de SUPEMIR',
      supemirDescription: 'SUPEMIR (Supérieur Privé Marocain d\'Informatique Recherche) est une école supérieure privée marocaine reconnue par l\'État, autorisée par le Ministère de l\'Enseignement Supérieur depuis 2004. Basée à Casablanca, SUPEMIR se spécialise dans la formation en informatique, management et commerce, offrant des diplômes reconnus allant du Bac+2 au Bac+5.',
      
      supemirMission: 'Mission',
      supemirMissionText: 'Former des professionnels compétents, innovants et éthiques, capables de répondre aux exigences du marché du travail national et international, tout en contribuant au développement socio-économique du Maroc.',
      
      supemirVision: 'Vision',
      supemirVisionText: 'Être un modèle d\'excellence éducative en combinant les meilleures pratiques pédagogiques internationales avec une approche innovante, formant des citoyens du monde capables de penser de manière critique et de contribuer positivement à la société.',
      
      supemirValues: 'Valeurs Fondamentales',
      valueHumanisme: 'Humanisme',
      valueHumanismeDesc: 'Placer l\'apprenant au cœur de nos préoccupations et favoriser son épanouissement personnel et professionnel',
      valueEntrepreneuriat: 'Entrepreneuriat',
      valueEntrepreneuriatDesc: 'Encourager l\'esprit d\'initiative, l\'innovation responsable et la création de valeur',
      valueProfessionnalisme: 'Professionnalisme',
      valueProfessionnalismeDesc: 'Viser l\'excellence éducative à travers la rigueur, la qualité et l\'amélioration continue',
      
      campusPhotos: 'Campus SUPEMIR',
      visitWebsite: 'Visiter le site web',
      
      // Développement
      myRoleTitle: 'Mon Rôle & Responsabilités',
      roleSubtitle: 'Développeur Full Stack Principal & Membre Équipe IT SUPEMIR',
      roleIntro: 'En tant que développeur principal et membre intégré de l\'équipe IT de SUPEMIR, j\'ai piloté l\'intégralité du cycle de développement de cette plateforme ERP éducative. Voici mes responsabilités détaillées :',
      
      responsibilities: [
        'Analyse approfondie des besoins avec la direction et les différents départements',
        'Conception de l\'architecture technique complète (Frontend, Backend, Base de données)',
        'Développement de l\'interface utilisateur avec React.js et design responsive',
        'Création d\'un système de routage et navigation complexe multi-rôles',
        'Développement Backend avec Node.js/Express et API RESTful',
        'Conception et implémentation de la base de données MongoDB avec schémas optimisés',
        'Implémentation du système d\'authentification JWT sécurisé avec gestion des sessions',
        'Développement du système de chat temps réel avec Socket.io et gestion des notifications',
        'Création du dashboard financier avancé avec statistiques et rapports dynamiques',
        'Mise en place de la logique métier complexe (calculs moyennes, échéances, validations)',
        'Tests unitaires, d\'intégration et tests utilisateurs sur le terrain',
        'Déploiement de la solution et configuration serveur',
        'Formation complète du personnel administratif et des utilisateurs finaux'
      ],
      
      techStackTitle: 'Stack Technique Utilisée',
      frontend: 'Frontend',
      frontendTech: ['React.js 18', 'React Router', 'Context API', 'Tailwind CSS', 'Axios'],
      backend: 'Backend',
      backendTech: ['Node.js', 'Express.js', 'Socket.io', 'JWT', 'Bcrypt'],
      database: 'Base de données',
      databaseTech: ['MongoDB', 'Mongoose ODM'],
      tools: 'Outils',
      toolsTech: ['Git/GitHub', 'Postman', 'VS Code', 'MongoDB Compass'],
      
      // Architecture
      architectureTitle: 'Architecture du Système',
      architectureDesc: 'Architecture client-serveur modulaire avec séparation claire des responsabilités, communication temps réel et gestion centralisée des données.',
      
      layersTitle: 'Couches Applicatives',
      presentationLayer: 'Couche Présentation',
      presentationLayerDesc: 'Interface utilisateur React.js responsive avec 6 interfaces différentes selon les rôles',
      businessLayer: 'Couche Métier',
      businessLayerDesc: 'API REST Node.js/Express gérant la logique métier, validations et calculs complexes',
      dataLayer: 'Couche Données',
      dataLayerDesc: 'Base MongoDB avec 15+ collections optimisées et relations structurées',
      realtimeLayer: 'Communication Temps Réel',
      realtimeLayerDesc: 'Socket.io pour chat instantané et notifications push',
      
      modulesTitle: 'Modules du Système',
      modulesSubtitle: '7 Modules Interconnectés',
      
      modules: [
        {
          name: 'Module Administration',
          description: 'Dashboard global avec vue d\'ensemble, gestion complète des utilisateurs (création, modification, suppression), gestion des filières et niveaux académiques, création et gestion des emplois du temps, configuration système et paramètres généraux'
        },
        {
          name: 'Module Pédagogique',
          description: 'Validation et gestion des préinscriptions, répartition intelligente des étudiants dans les classes, suivi pédagogique et progression des étudiants, gestion des matières et coefficients, création et suivi des examens'
        },
        {
          name: 'Module Commercial',
          description: 'Enregistrement et gestion des candidatures, système de sélection intelligent des candidats, tests d\'orientation (français/anglais), suivi du processus d\'admission, statistiques de conversion'
        },
        {
          name: 'Module Scolarité',
          description: 'Gestion complète des absences (saisie, justification, alertes), suivi administratif des étudiants, génération automatique de documents et attestations, gestion des sanctions disciplinaires, historique complet par étudiant'
        },
        {
          name: 'Module Comptabilité',
          description: 'Suivi détaillé des paiements et échéances, calcul automatique des montants selon les filières, génération de reçus et factures, rapports financiers détaillés, statistiques de recouvrement, alertes paiements en retard'
        },
        {
          name: 'Module Enseignant',
          description: 'Consultation et gestion des cours assignés, saisie des notes et moyennes, gestion des absences étudiants, communication avec étudiants via chat, accès aux emplois du temps'
        },
        {
          name: 'Module Étudiant',
          description: 'Consultation emploi du temps personnel, consultation des notes et moyennes, suivi des absences, tests et examens en ligne, messagerie avec professeurs, consultation résultats et bulletins'
        }
      ],
      
      // Fonctionnalités
      featuresTitle: 'Fonctionnalités Clés',
      
      features: [
        {
          icon: Users,
          title: 'Système Multi-Rôles Professionnel',
          description: 'Architecture complète avec 6 rôles utilisateurs distincts (Administrateur, Directeur, Responsable Pédagogique, Professeur, Étudiant, Comptable). Chaque rôle possède des permissions granulaires spécifiques, un dashboard personnalisé et un accès contrôlé aux fonctionnalités. Le système gère intelligemment les droits d\'accès avec une sécurité renforcée et une traçabilité complète de toutes les actions.'
        },
        {
          icon: BarChart3,
          title: 'Dashboard Financier Avancé',
          description: 'Système comptable complet avec suivi en temps réel des paiements, calcul automatique des échéances selon les filières, génération automatique de reçus personnalisés, statistiques financières détaillées avec graphiques interactifs, rapports de recouvrement par période, alertes automatiques pour paiements en retard, et historique complet des transactions. Permet une gestion financière rigoureuse et transparente.'
        },
        {
          icon: MessageCircle,
          title: 'Chat Temps Réel Intégré',
          description: 'Système de messagerie instantanée type WhatsApp développé avec Socket.io permettant la communication en temps réel entre tous les utilisateurs. Fonctionnalités incluant : envoi/réception instantané de messages, notifications push en temps réel, historique complet des conversations, indicateurs de lecture, création de groupes de discussion, et interface utilisateur fluide et intuitive.'
        },
        {
          icon: CheckCircle,
          title: 'Système d\'Inscription Intelligent',
          description: 'Processus d\'inscription digitalisé complet depuis la préinscription jusqu\'à l\'admission finale. Inclut un formulaire intelligent avec validation automatique des données, système de tests d\'orientation en français et anglais avec correction automatique, workflow de validation multi-étapes, notifications automatiques aux candidats, et tableau de bord pour le suivi des candidatures en temps réel.'
        },
        {
          icon: FileText,
          title: 'Gestion Pédagogique Complète',
          description: 'Module pédagogique exhaustif couvrant tous les aspects académiques : création et gestion des emplois du temps avec détection automatique des conflits, répartition intelligente des étudiants dans les classes selon les critères définis, suivi détaillé des absences avec alertes automatiques, gestion des notes avec calcul automatique des moyennes et coefficients, génération de bulletins et relevés de notes, et statistiques de performance par classe et matière.'
        },
        {
          icon: Shield,
          title: 'Sécurité Niveau Entreprise',
          description: 'Architecture de sécurité robuste avec authentification JWT (JSON Web Tokens) sécurisée, chiffrement bcrypt des mots de passe avec salting, gestion des sessions avec expiration automatique, protection CSRF et XSS, validation stricte des données côté serveur, logs détaillés de toutes les activités sensibles, système de permissions granulaires, et protection contre les injections et attaques courantes.'
        }
      ],
      
      // Gallery
      galleryTitle: 'Galerie & Démonstration',
      demoVideo: 'Vidéo de Démonstration',
      demoDescription: 'Découvrez la plateforme en action à travers cette démonstration complète montrant les différents modules et fonctionnalités',
      screenshots: 'Captures d\'Écran',
      screenshotsDescription: 'Aperçu des différentes interfaces de la plateforme',
      
      // Résultats
      resultsTitle: 'Impact & Résultats',
      
      statsTitle: 'Chiffres Clés',
      stats: [
        { value: '500+', label: 'Étudiants Gérés' },
        { value: '25+', label: 'Professeurs Actifs' },
        { value: '70%', label: 'Réduction Temps Admin' },
        { value: '100%', label: 'Taux d\'Adoption' }
      ],
      
      challengesMetTitle: 'Défis Techniques Relevés',
      challengesMet: [
        'Conception d\'une architecture scalable capable de gérer une croissance future importante',
        'Développement d\'une interface intuitive adaptée à 6 profils utilisateurs très différents',
        'Implémentation d\'un système de permissions complexe avec sécurité maximale',
        'Optimisation des performances pour gérer des requêtes en temps réel sur de gros volumes',
        'Synchronisation temps réel entre tous les modules sans perte de données',
        'Garantir une disponibilité 24/7 avec gestion des erreurs et récupération automatique'
      ],
      
      concreteResultsTitle: 'Résultats Concrets Mesurables',
      concreteResults: [
        'Réduction de 70% du temps de traitement des tâches administratives',
        'Élimination complète des erreurs de paiement grâce au système automatisé',
        'Communication école-famille améliorée avec satisfaction de 100%',
        'Génération instantanée de rapports qui prenaient auparavant des heures',
        'Adoption complète par l\'ensemble du personnel en seulement 2 semaines',
        'Retour sur investissement positif dès les 6 premiers mois d\'utilisation'
      ],
      
      testimonialTitle: 'Témoignage SUPEMIR',
      testimonialText: 'Cette plateforme a véritablement transformé notre façon de gérer l\'école. La vision technique et la capacité d\'exécution d\'Abdelmajid ont largement dépassé nos attentes. Son intégration dans notre équipe IT et sa compréhension profonde de nos besoins ont fait toute la différence. C\'est un atout majeur pour SUPEMIR.',
      testimonialAuthor: '- Direction SUPEMIR'
    },
    ar: {
      title: 'منصة إدارة مدرسية SUPEMIR',
      subtitle: 'حل ERP تعليمي احترافي',
      backButton: 'العودة للمشاريع',
      
      tabPresentation: 'التقديم',
      tabAboutSchool: 'SUPEMIR',
      tabDevelopment: 'التطوير',
      tabArchitecture: 'الهندسة',
      tabFeatures: 'المميزات',
      tabGallery: 'المعرض',
      tabResults: 'النتائج',
      
      presentationTitle: 'السياق والمبرر',
      presentationIntro: 'SUPEMIR مؤسسة تعليم عالي معترف بها منذ 2004، كانت تدير عملياتها بعمليات يدوية وأدوات مجزأة.',
      
      trustSection: 'مشروع قائم على الثقة',
      trustText: 'أعطتني SUPEMIR ثقة كاملة في تطوير هذا المشروع الاستراتيجي، مما يعكس فلسفة المدرسة في الإيمان بطلابها.',
      
      proudMoment: 'فخر واعتزاز',
      proudText: 'شرف كبير أن أكون مختاراً لتطوير منصة استراتيجية لمدرستي - منتج 100% SUPEMIR من طالب SUPEMIR.',
      
      challengesTitle: 'المشاكل المحددة',
      challenges: [
        'إدارة يدوية للتسجيلات تؤدي إلى أخطاء وضياع الوقت',
        'غياب نظام مركزي للمتابعة الأكاديمية والمالية',
        'تواصل غير فعال بين الإدارة والأساتذة والطلاب',
        'صعوبات في إنشاء التقارير والإحصائيات',
        'عملية دفع يدوية مصدر للأخطاء المحاسبية',
        'نقص في التتبع والسجل التاريخي'
      ],
      
      solutionTitle: 'الحل المطور',
      solutionText: 'قمت بتصميم وتطوير منصة ERP تعليمية كاملة ومتكاملة.',
      
      projectInfoTitle: 'معلومات المشروع',
      projectCommercial: 'مشروع تجاري',
      projectDuration: 'المدة: 4 أشهر',
      projectTeam: 'التطوير: فردي',
      projectStatus: 'الحالة: مكتمل ومنتشر في الإنتاج',
      
      aboutSupemir: 'عن SUPEMIR',
      supemirDescription: 'SUPEMIR مدرسة عليا خاصة مغربية معترف بها من قبل الدولة منذ 2004.',
      
      supemirMission: 'المهمة',
      supemirMissionText: 'تكوين محترفين أكفاء ومبتكرين وأخلاقيين.',
      
      supemirVision: 'الرؤية',
      supemirVisionText: 'أن نكون نموذجاً للتميز التعليمي.',
      
      supemirValues: 'القيم الأساسية',
      valueHumanisme: 'الإنسانية',
      valueHumanismeDesc: 'وضع المتعلم في قلب اهتماماتنا',
      valueEntrepreneuriat: 'ريادة الأعمال',
      valueEntrepreneuriatDesc: 'تشجيع الابتكار المسؤول',
      valueProfessionnalisme: 'الاحترافية',
      valueProfessionnalismeDesc: 'السعي للتميز التعليمي',
      
      campusPhotos: 'حرم SUPEMIR',
      visitWebsite: 'زيارة الموقع',
      
      myRoleTitle: 'دوري ومسؤولياتي',
      roleSubtitle: 'مطور Full Stack رئيسي',
      roleIntro: 'بصفتي مطوراً رئيسياً، قمت بقيادة دورة التطوير الكاملة.',
      
      responsibilities: [
        'تحليل الاحتياجات',
        'تصميم الهندسة',
        'تطوير الواجهة',
        'تطوير Backend',
        'قاعدة البيانات',
        'المصادقة JWT',
        'نظام الدردشة',
        'لوحة التحكم المالية',
        'الاختبار',
        'النشر',
        'التدريب'
      ],
      
      techStackTitle: 'التقنيات المستخدمة',
      frontend: 'الواجهة الأمامية',
      frontendTech: ['React.js 18', 'React Router', 'Context API', 'Tailwind CSS', 'Axios'],
      backend: 'الخادم',
      backendTech: ['Node.js', 'Express.js', 'Socket.io', 'JWT', 'Bcrypt'],
      database: 'قاعدة البيانات',
      databaseTech: ['MongoDB', 'Mongoose ODM'],
      tools: 'الأدوات',
      toolsTech: ['Git/GitHub', 'Postman', 'VS Code', 'MongoDB Compass'],
      
      architectureTitle: 'هندسة النظام',
      architectureDesc: 'هندسة معيارية مع فصل واضح للمسؤوليات.',
      
      layersTitle: 'الطبقات',
      presentationLayer: 'طبقة العرض',
      presentationLayerDesc: 'واجهة React.js متجاوبة',
      businessLayer: 'طبقة الأعمال',
      businessLayerDesc: 'API REST Node.js/Express',
      dataLayer: 'طبقة البيانات',
      dataLayerDesc: 'قاعدة MongoDB',
      realtimeLayer: 'الاتصال الفوري',
      realtimeLayerDesc: 'Socket.io للدردشة',
      
      modulesTitle: 'وحدات النظام',
      modulesSubtitle: '7 وحدات مترابطة',
      
      modules: [
        { name: 'وحدة الإدارة', description: 'لوحة تحكم عامة وإدارة المستخدمين' },
        { name: 'وحدة التربوية', description: 'التحقق من التسجيلات والتوزيع' },
        { name: 'وحدة التجارية', description: 'تسجيل الطلاب ونظام الاختيار' },
        { name: 'وحدة الدراسة', description: 'إدارة الغياب والمتابعة' },
        { name: 'وحدة المحاسبة', description: 'متابعة المدفوعات والتقارير' },
        { name: 'وحدة المعلم', description: 'إدارة الدروس والتواصل' },
        { name: 'وحدة الطالب', description: 'استشارة الجداول والنتائج' }
      ],
      
      featuresTitle: 'المميزات الرئيسية',
      
      features: [
        { icon: Users, title: 'نظام متعدد الأدوار', description: 'بنية كاملة مع 6 أدوار مستخدمين مختلفة' },
        { icon: BarChart3, title: 'لوحة مالية متقدمة', description: 'نظام محاسبي كامل مع متابعة المدفوعات' },
        { icon: MessageCircle, title: 'دردشة فورية', description: 'نظام مراسلة فورية باستخدام Socket.io' },
        { icon: CheckCircle, title: 'نظام تسجيل ذكي', description: 'عملية تسجيل رقمية كاملة' },
        { icon: FileText, title: 'إدارة تربوية', description: 'وحدة تربوية شاملة' },
        { icon: Shield, title: 'أمان على مستوى المؤسسة', description: 'بنية أمان قوية مع JWT' }
      ],
      
      galleryTitle: 'المعرض',
      demoVideo: 'فيديو توضيحي',
      demoDescription: 'اكتشف المنصة',
      screenshots: 'لقطات الشاشة',
      screenshotsDescription: 'نظرة على الواجهات',
      
      resultsTitle: 'التأثير والنتائج',
      
      statsTitle: 'أرقام رئيسية',
      stats: [
        { value: '500+', label: 'طالب' },
        { value: '25+', label: 'أستاذ' },
        { value: '70%', label: 'توفير الوقت' },
        { value: '100%', label: 'معدل التبني' }
      ],
      
      challengesMetTitle: 'التحديات التقنية',
      challengesMet: [
        'تصميم بنية قابلة للتوسع',
        'واجهة بديهية لـ 6 ملفات',
        'نظام أذونات معقد',
        'تحسين الأداء',
        'مزامنة فورية',
        'توفر 24/7'
      ],
      
      concreteResultsTitle: 'نتائج ملموسة',
      concreteResults: [
        'تقليل 70٪ من وقت المعالجة',
        'القضاء على أخطاء الدفع',
        'تحسين التواصل',
        'تقارير فورية',
        'التبني الكامل في أسبوعين',
        'عائد استثمار إيجابي'
      ],
      
      testimonialTitle: 'شهادة SUPEMIR',
      testimonialText: 'هذه المنصة غيرت طريقة إدارتنا للمدرسة.',
      testimonialAuthor: '- إدارة SUPEMIR'
    }
  };

  const t = content[language];

  const tabs = [
    { id: 'presentation', label: t.tabPresentation },
    { id: 'aboutSchool', label: t.tabAboutSchool },
    { id: 'development', label: t.tabDevelopment },
    { id: 'architecture', label: t.tabArchitecture },
    { id: 'features', label: t.tabFeatures },
    { id: 'gallery', label: t.tabGallery },
    { id: 'results', label: t.tabResults }
  ];

  const renderTabContent = () => {
    switch(activeTab) {
      case 'presentation':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 md:mb-6 text-blue-600">{t.presentationTitle}</h2>
              <p className={`text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6 md:mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.presentationIntro}
              </p>

              <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl mb-4 sm:mb-6 md:mb-8 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-600">{t.trustSection}</h3>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.trustText}
                </p>
              </div>

              <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl mb-4 sm:mb-6 md:mb-8 ${darkMode ? 'bg-gradient-to-r from-blue-900/20 to-purple-900/20' : 'bg-gradient-to-r from-blue-50 to-purple-50'} border-2 border-blue-600`}>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-600">{t.proudMoment}</h3>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.proudText}
                </p>
              </div>
              
              <div className="mb-4 sm:mb-6 md:mb-8">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-600">{t.challengesTitle}</h3>
                <div className="space-y-2 sm:space-y-3">
                  {t.challenges.map((challenge, index) => (
                    <div key={index} className="flex items-start gap-2 sm:gap-3">
                      <Zap size={16} className="text-blue-600 flex-shrink-0 mt-0.5 sm:mt-1 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                      <p className={`text-sm sm:text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-600">{t.solutionTitle}</h3>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.solutionText}
                </p>
              </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6 text-blue-600">{t.projectInfoTitle}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Target size={18} className="text-blue-600 flex-shrink-0 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <p className="font-bold text-xs sm:text-sm md:text-base lg:text-lg">{t.projectCommercial}</p>
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Calendar size={18} className="text-blue-600 flex-shrink-0 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <p className="font-bold text-xs sm:text-sm md:text-base lg:text-lg">{t.projectDuration}</p>
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Users size={18} className="text-blue-600 flex-shrink-0 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <p className="font-bold text-xs sm:text-sm md:text-base lg:text-lg">{t.projectTeam}</p>
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <CheckCircle size={18} className="text-blue-600 flex-shrink-0 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <p className="font-bold text-xs sm:text-sm md:text-base lg:text-lg">{t.projectStatus}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'aboutSchool':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
                <Building2 size={32} className="text-blue-600 sm:w-10 sm:h-10 md:w-12 md:h-12" />
                <div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-600">SUPEMIR</h2>
                  <p className={`text-xs sm:text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    École Supérieure Privée - Reconnue depuis 2004
                  </p>
                </div>
              </div>
              <p className={`text-sm sm:text-base md:text-lg leading-relaxed mb-3 sm:mb-4 md:mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.supemirDescription}
              </p>
              <a 
                href="https://supemir.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg text-xs sm:text-sm md:text-base"
              >
                <ExternalLink size={16} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
                {t.visitWebsite}
              </a>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 md:mb-4">
                  <Target size={24} className="text-blue-600 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">{t.supemirMission}</h3>
                </div>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.supemirMissionText}
                </p>
              </div>

              <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 md:mb-4">
                  <TrendingUp size={24} className="text-blue-600 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">{t.supemirVision}</h3>
                </div>
                <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t.supemirVisionText}
                </p>
              </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-6">
                <Award size={24} className="text-blue-600 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">{t.supemirValues}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h4 className="font-bold text-base sm:text-lg md:text-xl mb-2 text-blue-600">{t.valueHumanisme}</h4>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.valueHumanismeDesc}</p>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h4 className="font-bold text-base sm:text-lg md:text-xl mb-2 text-blue-600">{t.valueEntrepreneuriat}</h4>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.valueEntrepreneuriatDesc}</p>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h4 className="font-bold text-base sm:text-lg md:text-xl mb-2 text-blue-600">{t.valueProfessionnalisme}</h4>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.valueProfessionnalismeDesc}</p>
                </div>
              </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 md:mb-6 text-blue-600">{t.campusPhotos}</h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:gap-6">
                <div className="aspect-video rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300">
                  <img 
                    src="images/2025-07-19.webp" 
                    alt="Campus SUPEMIR" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'development':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-start sm:items-center gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6">
                <Code size={32} className="text-blue-600 flex-shrink-0 sm:w-10 sm:h-10 md:w-12 md:h-12" />
                <div>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">{t.myRoleTitle}</h2>
                  <p className={`text-sm sm:text-base md:text-lg font-semibold mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.roleSubtitle}</p>
                </div>
              </div>
              <p className={`text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-5 md:mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.roleIntro}
              </p>
              <div className="space-y-2 sm:space-y-3">
                {t.responsibilities.map((responsibility, index) => (
                  <div key={index} className="flex items-start gap-2 sm:gap-3">
                    <CheckCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5 sm:mt-1 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                    <p className={`text-sm sm:text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{responsibility}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 md:mb-8 text-blue-600">{t.techStackTitle}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h3 className="font-bold text-base sm:text-lg md:text-xl mb-3 sm:mb-4 text-blue-600">{t.frontend}</h3>
                  <div className="space-y-2">
                    {t.frontendTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-xs sm:text-sm md:text-base ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h3 className="font-bold text-base sm:text-lg md:text-xl mb-3 sm:mb-4 text-blue-600">{t.backend}</h3>
                  <div className="space-y-2">
                    {t.backendTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-xs sm:text-sm md:text-base ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h3 className="font-bold text-base sm:text-lg md:text-xl mb-3 sm:mb-4 text-blue-600">{t.database}</h3>
                  <div className="space-y-2">
                    {t.databaseTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-xs sm:text-sm md:text-base ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <h3 className="font-bold text-base sm:text-lg md:text-xl mb-3 sm:mb-4 text-blue-600">{t.tools}</h3>
                  <div className="space-y-2">
                    {t.toolsTech.map((tech, i) => (
                      <div key={i} className={`px-3 py-2 rounded text-xs sm:text-sm md:text-base ${darkMode ? 'bg-gray-700' : 'bg-white'} font-medium`}>{tech}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'architecture':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-blue-600">{t.architectureTitle}</h2>
              <p className={`text-sm sm:text-base md:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t.architectureDesc}
              </p>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 md:mb-6 text-blue-600">{t.layersTitle}</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Layers size={20} className="text-blue-600 sm:w-6 sm:h-6" />
                    <h4 className="font-bold text-base sm:text-lg md:text-xl text-blue-600">{t.presentationLayer}</h4>
                  </div>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.presentationLayerDesc}</p>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Settings size={20} className="text-blue-600 sm:w-6 sm:h-6" />
                    <h4 className="font-bold text-base sm:text-lg md:text-xl text-blue-600">{t.businessLayer}</h4>
                  </div>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.businessLayerDesc}</p>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Database size={20} className="text-blue-600 sm:w-6 sm:h-6" />
                    <h4 className="font-bold text-base sm:text-lg md:text-xl text-blue-600">{t.dataLayer}</h4>
                  </div>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.dataLayerDesc}</p>
                </div>
                <div className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <Zap size={20} className="text-blue-600 sm:w-6 sm:h-6" />
                    <h4 className="font-bold text-base sm:text-lg md:text-xl text-blue-600">{t.realtimeLayer}</h4>
                  </div>
                  <p className={`text-xs sm:text-sm md:text-base ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t.realtimeLayerDesc}</p>
                </div>
              </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 text-blue-600">{t.modulesTitle}</h3>
              <p className={`text-sm sm:text-base md:text-lg mb-4 sm:mb-5 md:mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t.modulesSubtitle}</p>
              <div className="space-y-3 sm:space-y-4">
                {t.modules.map((module, index) => (
                  <div key={index} className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-blue-600`}>
                    <h4 className="font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 text-blue-600">{module.name}</h4>
                    <p className={`text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {module.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 md:mb-8 text-blue-600">{t.featuresTitle}</h2>
              <div className="space-y-4 sm:space-y-5 md:space-y-6">
                {t.features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-blue-600`}>
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                        <div className="p-2 sm:p-2.5 md:p-3 rounded-lg bg-blue-600 flex-shrink-0">
                          <IconComponent size={24} color="white" className="sm:w-7 sm:h-7" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-base sm:text-lg md:text-xl mb-2 sm:mb-3 text-blue-600">{feature.title}</h3>
                          <p className={`text-xs sm:text-sm md:text-base lg:text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-blue-600">{t.screenshots}</h2>
              <p className={`text-sm sm:text-base md:text-lg mb-4 sm:mb-5 md:mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t.screenshotsDescription}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <div className="rounded-lg sm:rounded-xl overflow-hidden border-2 border-gray-300 shadow-lg">
                  <img 
                    src="images/Screenshot 2025-12-13 142407.png" 
                    alt="Aperçu Plateforme SUPEMIR" 
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>
              <p className={`text-xs sm:text-sm mt-4 text-center ${darkMode ? 'text-gray-500' : 'text-gray-500'} italic`}>
                * Les données sensibles (chiffres d'affaires, informations confidentielles) ont été masquées pour des raisons de confidentialité
              </p>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="space-y-4 sm:space-y-6 lg:space-y-8">
            <div className={`p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white shadow-lg'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-center text-blue-600">{t.resultsTitle}</h2>
              
              <div className="mb-8 sm:mb-10 md:mb-12">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 md:mb-6 text-blue-600">{t.statsTitle}</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                  {t.stats.map((stat, index) => (
                    <div key={index} className={`text-center p-4 sm:p-5 md:p-6 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-2 border-blue-600`}>
                      <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                      <div className={`text-xs sm:text-sm md:text-base font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6 sm:mb-7 md:mb-8">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 md:mb-6 text-blue-600">{t.challengesMetTitle}</h3>
                <div className="space-y-2 sm:space-y-3">
                  {t.challengesMet.map((challenge, index) => (
                    <div key={index} className="flex items-start gap-2 sm:gap-3">
                      <Zap size={18} className="text-blue-600 flex-shrink-0 mt-0.5 sm:mt-1 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                      <p className={`text-sm sm:text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{challenge}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6 sm:mb-7 md:mb-8">
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-5 md:mb-6 text-blue-600">{t.concreteResultsTitle}</h3>
                <div className="space-y-2 sm:space-y-3">
                  {t.concreteResults.map((result, index) => (
                    <div key={index} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5 sm:mt-1 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                      <p className={`text-sm sm:text-base md:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{result}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`p-4 sm:p-6 md:p-8 rounded-lg sm:rounded-xl ${darkMode ? 'bg-blue-900/30' : 'bg-blue-50'} border-l-4 border-blue-600`}>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 text-blue-600">{t.testimonialTitle}</h3>
                <p className={`text-base sm:text-lg md:text-xl italic leading-relaxed mb-3 sm:mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  "{t.testimonialText}"
                </p>
                <p className={`font-bold text-sm sm:text-base md:text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t.testimonialAuthor}
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-3 sm:py-6 md:py-8`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 mb-4 sm:mb-6 md:mb-8 px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg ${
            darkMode ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-100 text-gray-900 shadow-md'
          } transition-colors font-semibold text-xs sm:text-sm md:text-base`}
        >
          <ArrowLeft size={16} className="sm:w-4 sm:h-4 md:w-5 md:h-5" />
          <span>{t.backButton}</span>
        </button>

        <div className="text-center mb-6 sm:mb-10 md:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-3 md:mb-4 text-blue-600 px-2">
            {t.title}
          </h1>
          <p className={`text-sm sm:text-base md:text-lg lg:text-xl font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'} px-2`}>
            {t.subtitle}
          </p>
        </div>

        <div className="mb-4 sm:mb-6 md:mb-8 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <div className="flex gap-2 min-w-max pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg font-semibold transition-all whitespace-nowrap text-xs sm:text-sm md:text-base ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : darkMode
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300'
                    : 'bg-white hover:bg-gray-100 text-gray-700 shadow-md'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}